import type { GenerationLayoutSourceKind } from "./types";

const CANVAS_PANEL_INLINE_LAYOUT_TRIGGERS = [
  "canvas_render",
  "canvas_video",
  "canvas_matting",
  "canvas_outpaint",
  "canvas_inpaint",
  "canvas_draft",
  "canvas_repaint_upscale",
  "canvas_free_perspective",
] as const;

const IMAGE_CONTEXT_SOURCES = new Set([
  "quick_edit_tab",
  "canvas_render",
  "canvas_video",
  "canvas_matting",
  "canvas_outpaint",
  "canvas_inpaint",
  "canvas_draft",
  "canvas_repaint_upscale",
  "canvas_free_perspective",
  "canvas_fission",
  "dir_regenerate",
]);

function parseAttachObject(raw: unknown): Record<string, unknown> {
  if (!raw) return {};
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === "object" ? (parsed as Record<string, unknown>) : {};
    } catch {
      return {};
    }
  }
  return typeof raw === "object" ? (raw as Record<string, unknown>) : {};
}

/**
 * 从任务 params 解析排布来源。
 * 优先读显式 `_generationLayoutSource`，否则按 canvas_trigger / attach 推断。
 */
export function resolveGenerationLayoutSourceKind(
  params?: Record<string, unknown> | null
): GenerationLayoutSourceKind {
  if (!params) return "bottom_hero";

  const explicit = params._generationLayoutSource;
  if (explicit === "bottom_hero" || explicit === "image_context") {
    return explicit;
  }

  const trigger = String(params.canvas_trigger ?? "");
  if (trigger === "bottom_hero_actions") return "bottom_hero";

  const attach = parseAttachObject(params.attach);
  if (attach._videoTaskSource === "bottom_hero_actions") return "bottom_hero";
  if (attach._videoTaskSource === "video_panel") return "image_context";

  if ((CANVAS_PANEL_INLINE_LAYOUT_TRIGGERS as readonly string[]).includes(trigger)) {
    return "image_context";
  }
  if (IMAGE_CONTEXT_SOURCES.has(trigger)) return "image_context";

  // 底部 3D：canvas_3d 且无面板锚点 targetCid
  if (trigger === "canvas_3d" && !hasPanelAnchorTarget(params)) {
    return "bottom_hero";
  }

  return "bottom_hero";
}

function hasPanelAnchorTarget(params: Record<string, unknown>): boolean {
  const keys = [
    "_renderTargetCid",
    "_videoTargetCid",
    "_inpaintTargetCid",
    "_outpaintTargetCid",
    "_draftTargetCid",
    "_repaintUpscaleTargetCid",
    "_freePerspectiveTargetCid",
    "_panelLayoutAnchorCid",
  ];
  return keys.some((key) => Boolean(params[key]));
}

export function stampGenerationLayoutSource(
  params: Record<string, unknown>,
  sourceKind: GenerationLayoutSourceKind
): Record<string, unknown> {
  return { ...params, _generationLayoutSource: sourceKind };
}
